import { Grid, MenuItem, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { Controller, FieldValues, FormProvider, useForm, useFormContext, Validate, } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import CustomAutocompleteFreesolo
  from "../../../common/components/functional/customAutocomplete/CustomAutocompleteFreeSolo";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import { getCoreUserFullName } from "../../../common/utils/users";
import { handleSalesAccountsData } from "../../settings/helpers/utils";
import { SalesAccount } from "../../settings/settingsApi";
import { fetchSalesAccounts } from "../customerContractsSlice";
import { createNewCustomerUser, selectAllCustomerUsers, selectCustomerUsersByCompanyId, } from "../customerUsersSlice";
import { CreateCustomerUser } from "../types/commands";
import { CustomerContact } from "../types/types";
import UserForm from "../users/UserForm";

type Props = {
  customerCompanyId: string;
};

const AdditionalInfo = ({ customerCompanyId }: Props) => {
  const [dialogValue, setDialogValue] = useState({ open: false, value: "" });
  const [open, setOpen] = useState(true);
  const [salesAccountOptions, setSalesAccountOptions] = useState<
    SalesAccount[]
  >([]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const currentDate = format(new Date(), isoFormat);
    dispatch(fetchSalesAccounts({ validFrom: currentDate })).then((action) => {
      if (isFulfilled(action)) {
        const { accounts } = action.payload;
        const accountsArray = handleSalesAccountsData(accounts);
        setSalesAccountOptions(
          accountsArray.filter((account) => account.canUseInContract),
        );
      }
    });
  }, [dispatch]);

  const customerUsersByCompanyId = useAppSelector(
    selectCustomerUsersByCompanyId(customerCompanyId),
  );
  const customerUsers = useAppSelector(selectAllCustomerUsers);

  const formattedCustomerUsers = useMemo(
    () =>
      customerUsersByCompanyId.map((customerUser) => ({
        id: customerUser.id,
        name: getCoreUserFullName(customerUser),
      })),
    [customerUsersByCompanyId],
  );

  const { t } = useTranslation();
  const { control, setValue } = useFormContext();

  const defaultValues: CustomerContact = {
    firstName: "",
    lastName: "",
    phone: "",
    emailAddress: "",
    title: "",
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const toggleOpen = () => setOpen(!open);
  const onClose = () => setDialogValue({ open: false, value: "" });

  const onSave = (customerContact: CustomerContact) => {
    const customerUser: CreateCustomerUser = {
      ...customerContact,
      customerCompanyId,
    };

    dispatch(createNewCustomerUser(customerUser)).then((action) => {
      if (isFulfilled(action)) {
        const customerUserCreated = action.payload.find(
          (event) => event.name === "custUsrCreated",
        );

        const formattedCustomerUser = {
          id: customerUserCreated?.id,
          name: `${customerUserCreated?.payload.firstName} ${customerUserCreated?.payload.lastName}`,
        };

        if (customerUserCreated) {
          setValue("invoiceContactPerson", formattedCustomerUser, {
            shouldDirty: true,
          });
          onClose();
        }
      }
    });
  };

  const validateEmailAddress: Validate<string, FieldValues> = (
    emailAddress: string,
  ) => {
    const isDirty = !!methods.formState.dirtyFields["emailAddress"];

    if (!isDirty) return true;

    // TODO: move validation to backend
    const isExistingContact = customerUsers.find(
      (customerUser) => customerUser.account.emailAddress === emailAddress,
    );

    if (isExistingContact) {
      const emailInUse = t("customers:usersNS:emailInUse");
      return emailInUse;
    }

    return true;
  };

  return (
    <FormSection
      title={t("common:additionalInfo")}
      collapse
      noBorder
      open={open}
      toggleOpen={toggleOpen}
      infoTooltip={
        <InfoTooltip
          style={{ marginLeft: "16px" }}
          title={t("customers:costCenters:additionalInfoTooltip")}
        />
      }
    >
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("customers:costCenters:contactPerson")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="invoiceContactPerson"
          control={control}
          render={({ field: { value, onChange } }) => (
            <CustomAutocompleteFreesolo
              value={value || null}
              options={formattedCustomerUsers.sort((a, b) =>
                a.name.localeCompare(b.name),
              )}
              onChange={(_, newValue, dialog) => {
                if (!dialog.dialogOpen) {
                  onChange(newValue);
                } else {
                  setDialogValue({ open: true, value: dialog.dialogValue });
                }
              }}
            />
          )}
        />
      </Grid>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("customers:costCenters:orderIdentifier")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="orderIdentifier"
          control={control}
          render={({ field }) => <CommonTextField {...field} fullWidth />}
        />
      </Grid>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("customers:costCenters:agreementIdentifier")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="agreementIdentifier"
          control={control}
          render={({ field }) => <CommonTextField {...field} fullWidth />}
        />
      </Grid>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("customers:costCenters:sellerReferenceIdentifier")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="sellerReferenceIdentifier"
          control={control}
          render={({ field }) => <CommonTextField {...field} fullWidth />}
        />
      </Grid>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("customers:costCenters:buyerReferenceIdentifier")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="buyerReferenceIdentifier"
          control={control}
          render={({ field }) => <CommonTextField {...field} fullWidth />}
        />
      </Grid>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("customers:costCenters:invoicing")}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Controller
          name="salesAccount"
          control={control}
          render={({ field }) => (
            <CommonTextField
              {...field}
              value={field.value?.code || ""}
              onChange={(e) => {
                const code = e.target.value;
                const option = salesAccountOptions.find(
                  (option) => option.code === code,
                );

                field.onChange(option);
              }}
              defaultValue=""
              select
              fullWidth
            >
              {salesAccountOptions.map((option) => (
                <MenuItem
                  key={option.code}
                  value={option.code}
                >{`${option.code} ${option?.description}`}</MenuItem>
              ))}
            </CommonTextField>
          )}
        />
      </Grid>
      <DialogComponent open={dialogValue.open} onClose={onClose}>
        <DialogContentWrapper
          margin
          title={t("customers:workLocationNS:addCustomerContactPerson")}
          onCancel={onClose}
          onSave={methods.handleSubmit(onSave)}
          saveDisabled={false}
        >
          <FormProvider {...methods}>
            <UserForm
              validateEmailAddress={validateEmailAddress}
              disablePrefill
            />
          </FormProvider>
        </DialogContentWrapper>
      </DialogComponent>
    </FormSection>
  );
};

export default AdditionalInfo;
